import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/supabaseTypes";

export interface EntitlementGrantInput {
  readonly ownerId: string;
  readonly entitlementKey: string;
  readonly grantSource: string;
  readonly sourceReference?: string | null;
}

export type EntitlementGrantOutcome = "granted" | "already-owned";

export interface PlayerEntitlementRecord {
  readonly entitlementKey: string;
  readonly grantSource: string;
  readonly grantedAt: string;
  readonly expiresAt: string | null;
}

export interface EntitlementRepository {
  grant(input: EntitlementGrantInput): Promise<EntitlementGrantOutcome>;
  listForOwner(ownerId: string): Promise<readonly PlayerEntitlementRecord[]>;
}

export function createSupabaseEntitlementRepository(
  client: SupabaseClient<Database>,
): EntitlementRepository {
  return {
    async grant(input) {
      const existing = await client
        .from("player_entitlements")
        .select("id")
        .eq("owner_id", input.ownerId)
        .eq("entitlement_key", input.entitlementKey)
        .maybeSingle();
      if (existing.error) {
        throw new Error(
          `Failed to read player_entitlements for owner ${input.ownerId}: ${existing.error.message}`,
        );
      }
      if (existing.data !== null) {
        return "already-owned";
      }
      const insert = await client.from("player_entitlements").insert({
        owner_id: input.ownerId,
        entitlement_key: input.entitlementKey,
        grant_source: input.grantSource,
        source_reference: input.sourceReference ?? null,
      });
      if (insert.error) {
        // unique-violation race: treat as already-owned for idempotency.
        if (insert.error.code === "23505") {
          return "already-owned";
        }
        throw new Error(
          `Failed to insert player_entitlements for owner ${input.ownerId}: ${insert.error.message}`,
        );
      }
      return "granted";
    },
    async listForOwner(ownerId) {
      const result = await client
        .from("player_entitlements")
        .select("entitlement_key, grant_source, granted_at, expires_at")
        .eq("owner_id", ownerId);
      if (result.error) {
        throw new Error(
          `Failed to list player_entitlements for owner ${ownerId}: ${result.error.message}`,
        );
      }
      const rows = result.data ?? [];
      return rows.map((row) => ({
        entitlementKey: row.entitlement_key,
        grantSource: row.grant_source,
        grantedAt: row.granted_at,
        expiresAt: row.expires_at,
      }));
    },
  };
}

export function createInMemoryEntitlementRepository(): EntitlementRepository {
  const byOwner = new Map<string, Map<string, PlayerEntitlementRecord>>();
  return {
    async grant(input) {
      const owner =
        byOwner.get(input.ownerId) ??
        new Map<string, PlayerEntitlementRecord>();
      if (owner.has(input.entitlementKey)) {
        return "already-owned";
      }
      owner.set(input.entitlementKey, {
        entitlementKey: input.entitlementKey,
        grantSource: input.grantSource,
        grantedAt: new Date().toISOString(),
        expiresAt: null,
      });
      byOwner.set(input.ownerId, owner);
      return "granted";
    },
    async listForOwner(ownerId) {
      const owner = byOwner.get(ownerId);
      if (owner === undefined) {
        return [];
      }
      return [...owner.values()];
    },
  };
}
